import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isJwtExpired } from '../../auth/jwt/tokenUtils';

interface User {
    id: string;
    email: string;
    name: string;
    firstName?: string;
    lastName?: string;
    role: string;
    avatar?: string;
}

interface AuthState {
    isAuthenticated: boolean;
    user: User | null;
    token: string | null;
}

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
};

const isAllowedFrontendRole = (role?: string) => role === 'user' || role === 'trainer';

const normalizeUser = (rawUser: any): User | null => {
    if (!rawUser || !rawUser.id || !rawUser.email || !rawUser.role) {
        return null;
    }

    const firstName = (rawUser.firstName || '').trim();
    const lastName = (rawUser.lastName || '').trim();
    const fullNameFromParts = `${firstName} ${lastName}`.trim();
    const name = (rawUser.name || '').trim() || fullNameFromParts;

    return {
        id: rawUser.id,
        email: rawUser.email,
        role: rawUser.role,
        avatar: rawUser.avatar || rawUser.profileImage || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        name,
    };
};

// Check localStorage for existing auth data
const storedAuth = localStorage.getItem('auth');
if (storedAuth) {
    try {
        const parsedAuth = JSON.parse(storedAuth);
        const token = parsedAuth.token;
        const normalizedUser = normalizeUser(parsedAuth.user);

        if (isAllowedFrontendRole(normalizedUser?.role) && token && !isJwtExpired(token)) {
            initialState.isAuthenticated = true;
            initialState.user = normalizedUser;
            initialState.token = token;

            localStorage.setItem('auth', JSON.stringify({
                user: normalizedUser,
                token,
                refreshToken: parsedAuth.refreshToken,
            }));
        } else {
            localStorage.removeItem('auth');
        }
    } catch (error) {
        console.error('Error parsing stored auth:', error);
        localStorage.removeItem('auth');
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        login: (state, action: PayloadAction<{ user: User; token: string }>) => {
            const normalizedUser = normalizeUser(action.payload.user);

            if (!isAllowedFrontendRole(normalizedUser?.role)) {
                throw new Error('Access denied. Member or trainer account required.');
            }

            state.isAuthenticated = true;
            state.user = normalizedUser;
            state.token = action.payload.token;

            // localStorage is already set by jwtService
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.token = null;

            // Clear localStorage
            localStorage.removeItem('auth');
        },
        updateUser: (state, action: PayloadAction<Partial<User>>) => {
            if (state.user) {
                const mergedUser = { ...state.user, ...action.payload };
                state.user = normalizeUser(mergedUser) || state.user;

                // Update localStorage
                localStorage.setItem('auth', JSON.stringify({
                    user: state.user,
                    token: state.token,
                }));
            }
        },
    },
});

export const { login, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
